import { cn } from '@up2stay/up2promo-ui';
import { TooltipProvider } from '@up2stay/up2promo-ui';
import * as React from 'react';

import { useShellSidebarOverlay } from '@/core/hooks/use-mobile';
import { getCookie, setCookie } from '@/core/utils/cookies';

export const SIDEBAR_COOKIE_NAME = 'sidebar_state';
export const SIDEBAR_WIDTH_COOKIE_NAME = 'sidebar_width';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
/** Default expanded width (16rem). */
export const SIDEBAR_WIDTH_DEFAULT_PX = 256;
export const SIDEBAR_WIDTH_MIN_PX = 200;
export const SIDEBAR_WIDTH_MAX_PX = 360;
export const SIDEBAR_WIDTH = `${SIDEBAR_WIDTH_DEFAULT_PX}px`;
export const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

function clampSidebarWidth(width: number): number {
  return Math.min(
    SIDEBAR_WIDTH_MAX_PX,
    Math.max(SIDEBAR_WIDTH_MIN_PX, Math.round(width)),
  );
}

function readStoredSidebarWidth(): number {
  if (typeof window === 'undefined') {
    return SIDEBAR_WIDTH_DEFAULT_PX;
  }
  const cookieValue = getCookie(SIDEBAR_WIDTH_COOKIE_NAME);
  if (cookieValue === null) {
    return SIDEBAR_WIDTH_DEFAULT_PX;
  }
  const parsed = Number.parseInt(cookieValue, 10);
  if (!Number.isFinite(parsed)) {
    return SIDEBAR_WIDTH_DEFAULT_PX;
  }
  return clampSidebarWidth(parsed);
}

export type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  /**
   * Overlay/drawer mode for the browser sidebar (viewport below xl / 1280).
   * Not the same as page `useIsMobile()` (768). Name kept for shadcn parity.
   */
  isMobile: boolean;
  toggleSidebar: () => void;
  /** Expanded sidebar width in px (desktop). */
  width: number;
  setWidth: (width: number, options?: { persist?: boolean }) => void;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

/** Non-throwing hook for chrome that may briefly render without a provider (e.g. Vite HMR). */
export function useSidebarOptional() {
  return React.useContext(SidebarContext);
}

export function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  // Overlay until xl — independent of page `useIsMobile()` (md). See app-conventions.
  const isMobile = useShellSidebarOverlay();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [width, _setWidth] = React.useState(readStoredSidebarWidth);
  const [isResizing, setIsResizing] = React.useState(false);

  const [_open, _setOpen] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const cookieValue = getCookie(SIDEBAR_COOKIE_NAME);
      if (cookieValue !== null) {
        return cookieValue !== 'false';
      }
      return true;
    }
    return defaultOpen;
  });
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      setCookie(SIDEBAR_COOKIE_NAME, String(openState), SIDEBAR_COOKIE_MAX_AGE);
    },
    [setOpenProp, open],
  );

  const setWidth = React.useCallback(
    (next: number, options?: { persist?: boolean }) => {
      const clamped = clampSidebarWidth(next);
      _setWidth(clamped);
      if (options?.persist === false) {
        return;
      }
      setCookie(
        SIDEBAR_WIDTH_COOKIE_NAME,
        String(clamped),
        SIDEBAR_COOKIE_MAX_AGE,
      );
    },
    [],
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      width,
      setWidth,
      isResizing,
      setIsResizing,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      width,
      setWidth,
      isResizing,
    ],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          data-resizing={isResizing ? 'true' : undefined}
          style={
            {
              '--sidebar-width': `${width}px`,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            'group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}
